import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, fmtMoney } from "@/components/transport/transportShared";
import { BadgeCheck, IndianRupee } from "lucide-react";

const PRESETS = [
  ["today", "Today"], ["week", "This Week"], ["month", "This Month"], ["all", "All Time"], ["custom", "Custom Dates"],
];

function presetRange(preset) {
  // Local-date (IST-safe) yyyy-mm-dd — toISOString would shift to UTC near midnight.
  const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  const d = new Date();
  if (preset === "today") return { date_from: iso(d), date_to: iso(d) };
  if (preset === "week") { const f = new Date(d); f.setDate(d.getDate() - 6); return { date_from: iso(f), date_to: iso(d) }; }
  if (preset === "month") return { date_from: iso(d).slice(0, 8) + "01", date_to: iso(d) };
  return { date_from: "", date_to: "" }; // all/custom: clear any lingering range
}

function Filters({ value, onChange, vehicles, drivers, transporters }) {
  const set = (k, v) => onChange({ ...value, [k]: v === "__all" ? "" : v });
  const SelectF = ({ k, placeholder, options }) => (
    <Select value={value[k] || "__all"} onValueChange={(v) => set(k, v)}>
      <SelectTrigger className="am-input h-9 text-xs w-full" data-testid={`fin-filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all">{placeholder}: All</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <div className="am-card p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="fin-filters">
      <Select value={value.preset} onValueChange={(v) => onChange({ ...value, preset: v, ...presetRange(v) })}>
        <SelectTrigger className="am-input h-9 text-xs" data-testid="fin-filter-preset"><SelectValue /></SelectTrigger>
        <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {value.preset === "custom" && (
        <>
          <Input type="date" className="am-input h-9 text-xs" data-testid="fin-filter-from" value={value.date_from || ""} onChange={(e) => set("date_from", e.target.value)} />
          <Input type="date" className="am-input h-9 text-xs" data-testid="fin-filter-to" value={value.date_to || ""} onChange={(e) => set("date_to", e.target.value)} />
        </>
      )}
      <SelectF k="area" placeholder="Area" options={[...new Set(vehicles.map((v) => v.operating_area))].map((a) => [a, a])} />
      <SelectF k="vehicle_id" placeholder="Vehicle" options={vehicles.map((v) => [v.vehicle_id, v.registration_number])} />
      <SelectF k="driver_id" placeholder="Driver" options={drivers.map((d) => [d.driver_id, d.name])} />
      <SelectF k="transporter_id" placeholder="Transporter" options={transporters.map((t) => [t.transporter_id, t.name])} />
    </div>
  );
}

const filterParams = ({ preset: _preset, ...rest }) => Object.fromEntries(Object.entries(rest).filter(([, v]) => v));

/* ============ PAYMENTS TAB (Commission dashboard + ledger) ============ */
export function PaymentsTab({ vehicles, drivers, transporters }) {
  const [filters, setFilters] = useState({ preset: "all" });
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paidFor, setPaidFor] = useState(null); // payment being marked paid
  const [txnRef, setTxnRef] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    const params = filterParams(filters);
    api.get("/transport/finance/summary", { params }).then((r) => setSummary(r.data)).catch(() => {});
    api.get("/transport/payments", { params }).then((r) => setPayments(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load payments"));
  }, [filters]);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const kpis = summary && [
    ["Total Transport Fare", fmtMoney(summary.total_fare)],
    ["AnimalMela Commission", fmtMoney(summary.total_commission)],
    ["Transporter Payment", fmtMoney(summary.total_payable)],
    ["Pending Payments", `${summary.pending_payments.count} · ${fmtMoney(summary.pending_payments.amount)}`],
    ["Paid Payments", `${summary.paid_payments.count} · ${fmtMoney(summary.paid_payments.amount)}`],
  ];

  return (
    <div className="space-y-4">
      <Filters value={filters} onChange={setFilters} vehicles={vehicles} drivers={drivers} transporters={transporters} />
      {kpis && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="fin-kpis">
          {kpis.map(([label, value]) => (
            <div key={label} className="am-card p-4" data-testid={`fin-kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
              <div className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">{label}</div>
              <div className="font-display text-xl font-extrabold mt-1">{value}</div>
            </div>
          ))}
        </div>
      )}
      <div className="am-card overflow-x-auto" data-testid="fin-payments-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              {["Trip", "Vehicle / Driver", "Transporter", "Fare", "Comm %", "Commission", "Payable", "Status", "Actions"].map((c) => (
                <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.payment_id} className="border-b last:border-0" data-testid={`fin-payment-row-${p.payment_id}`}>
                <td className="px-3 py-3 whitespace-nowrap"><span className="font-mono text-xs">{p.trip_id.slice(0, 8)}</span>
                  <div className="text-xs text-muted-foreground">{p.pickup_area} → {p.destination} · {(p.completed_at || "").slice(0, 10)}</div></td>
                <td className="px-3 py-3 whitespace-nowrap font-bold">{p.vehicle}<div className="text-xs font-normal text-muted-foreground">{p.driver}</div></td>
                <td className="px-3 py-3 whitespace-nowrap">{p.transporter}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(p.fare)}</td>
                <td className="px-3 py-3">{p.commission_percent}%</td>
                <td className="px-3 py-3 whitespace-nowrap" data-testid={`fin-commission-${p.payment_id}`}>{fmtMoney(p.commission)}</td>
                <td className="px-3 py-3 whitespace-nowrap font-bold" data-testid={`fin-payable-${p.payment_id}`}>{fmtMoney(p.payable)}</td>
                <td className="px-3 py-3"><StatusBadge status={p.status} />
                  {p.status === "PAID" && p.transaction_reference ? <div className="text-[10px] text-muted-foreground mt-0.5">ref: {p.transaction_reference}</div> : null}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-1.5 justify-end">
                    {p.status === "PENDING" && (
                      <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy} data-testid={`fin-approve-${p.payment_id}`}
                        onClick={() => act(() => api.post(`/transport/payments/${p.payment_id}/approve`), "Payment approved")}>
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Approve
                      </Button>
                    )}
                    {p.status === "APPROVED" && (
                      <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy} data-testid={`fin-paid-${p.payment_id}`}
                        onClick={() => { setPaidFor(p); setTxnRef(""); }}>
                        <IndianRupee className="w-3.5 h-3.5 mr-1" /> Mark Paid
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {payments.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No payments for the selected filters.</p>}
      </div>

      <Dialog open={!!paidFor} onOpenChange={() => setPaidFor(null)}>
        <DialogContent className="max-w-sm" data-testid="fin-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark Payment Paid</DialogTitle>
            <DialogDescription>
              {paidFor && <>Pay <b>{fmtMoney(paidFor.payable)}</b> to {paidFor.transporter} ({paidFor.vehicle})</>}
            </DialogDescription>
          </DialogHeader>
          <Input className="am-input" placeholder="Transaction reference (UPI/NEFT ref)" data-testid="fin-txn-ref"
            value={txnRef} onChange={(e) => setTxnRef(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setPaidFor(null)}>Cancel</Button>
            <Button className="am-btn-primary" disabled={busy || !txnRef.trim()} data-testid="fin-paid-confirm"
              onClick={() => act(async () => {
                await api.post(`/transport/payments/${paidFor.payment_id}/mark-paid`, { transaction_reference: txnRef.trim() });
                setPaidFor(null);
              }, "Payment marked PAID")}>
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ REPORTS TAB ============ */
const REPORT_TYPES = [
  ["daily", "Daily Transport Report"], ["monthly", "Monthly Transport Report"], ["vehicle", "Vehicle Report"],
  ["driver", "Driver Report"], ["area", "Area Report"], ["payment", "Payment Report"],
];

export function ReportsTab() {
  const [groupBy, setGroupBy] = useState("daily");
  const [filters, setFilters] = useState({ preset: "all" });
  const [report, setReport] = useState(null);

  useEffect(() => {
    api.get("/transport/reports", { params: { group_by: groupBy, ...filterParams(filters) } })
      .then((r) => setReport(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load report"));
  }, [groupBy, filters]);

  return (
    <div className="space-y-4">
      <div className="am-card p-4 flex flex-wrap gap-2 items-center">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="am-input h-9 text-xs w-56" data-testid="rep-type"><SelectValue /></SelectTrigger>
          <SelectContent>{REPORT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.preset} onValueChange={(v) => setFilters({ preset: v, ...presetRange(v) })}>
          <SelectTrigger className="am-input h-9 text-xs w-36" data-testid="rep-preset"><SelectValue /></SelectTrigger>
          <SelectContent>{PRESETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {filters.preset === "custom" && (
          <>
            <Input type="date" className="am-input h-9 text-xs w-40" value={filters.date_from || ""} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            <Input type="date" className="am-input h-9 text-xs w-40" value={filters.date_to || ""} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </>
        )}
      </div>
      <div className="am-card overflow-x-auto" data-testid="rep-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              {[REPORT_TYPES.find(([v]) => v === groupBy)?.[1].replace(" Report", "").replace(" Transport", ""), "Trips", "Animals", "Transport Fare", "Commission", "Transporter Payment"].map((c) => (
                <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(report?.rows || []).map((r) => (
              <tr key={r.key} className="border-b last:border-0" data-testid={`rep-row-${r.key}`}>
                <td className="px-3 py-3 font-bold whitespace-nowrap">{groupBy === "payment" ? <StatusBadge status={r.key} /> : r.key}</td>
                <td className="px-3 py-3">{r.trips}</td>
                <td className="px-3 py-3">{r.animals}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(r.fare)}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(r.commission)}</td>
                <td className="px-3 py-3 whitespace-nowrap font-bold">{fmtMoney(r.payable)}</td>
              </tr>
            ))}
            {report && report.rows.length > 0 && (
              <tr className="bg-muted font-bold" data-testid="rep-totals">
                <td className="px-3 py-3">TOTAL</td>
                <td className="px-3 py-3">{report.totals.trips}</td>
                <td className="px-3 py-3">{report.totals.animals}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(report.totals.fare)}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(report.totals.commission)}</td>
                <td className="px-3 py-3 whitespace-nowrap">{fmtMoney(report.totals.payable)}</td>
              </tr>
            )}
          </tbody>
        </table>
        {report && report.rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No completed trips in this range.</p>}
      </div>
    </div>
  );
}
